if (!customElements.get('scroll-frame-sequence')) {
  customElements.define(
    'scroll-frame-sequence',
    class ScrollFrameSequence extends HTMLElement {
      connectedCallback() {
        this.scrollArea = this.querySelector('.scroll-frame-sequence__scroll-area');
        this.sticky = this.querySelector('.scroll-frame-sequence__sticky');
        this.stage = this.querySelector('.scroll-frame-sequence__stage');
        this.canvas = this.querySelector('.scroll-frame-sequence__canvas');
        this.fallbackImage = this.querySelector('.scroll-frame-sequence__fallback');
        this.loadingIndicator = this.querySelector('.scroll-frame-sequence__loading');
        this.endGrid = this.querySelector('[data-sequence-end-grid]');

        if (!this.scrollArea || !this.sticky || !this.stage || !this.canvas) return;

        this.context = this.canvas.getContext('2d');
        if (!this.context) return;

        this.baseUrl = (this.dataset.baseUrl || '').trim();
        this.filePrefix = this.dataset.filePrefix || '';
        this.fileExtension = (this.dataset.fileExtension || 'webp').replace(/^\./, '');
        this.cacheVersion = (this.dataset.cacheVersion || '').trim();
        this.gridRevealStart = Number.parseFloat(this.dataset.gridRevealStart);
        this.startFrame = Number.parseInt(this.dataset.startFrame, 10);
        this.endFrame = Number.parseInt(this.dataset.endFrame, 10);
        this.objectFit = this.dataset.objectFit === 'cover' ? 'cover' : 'contain';

        const mobileBaseUrl = (this.dataset.mobileBaseUrl || '').trim();
        const mobileFilePrefix = this.dataset.mobileFilePrefix || '';
        const mobileStartFrame = Number.parseInt(this.dataset.mobileStartFrame, 10);
        const mobileEndFrame = Number.parseInt(this.dataset.mobileEndFrame, 10);
        const mobileObjectFit = this.dataset.mobileObjectFit === 'contain' ? 'contain' : 'cover';
        const mobileBreakpoint = Number.parseInt(this.dataset.mobileBreakpoint, 10) || 749;
        const isMobileViewport = typeof window.matchMedia === 'function'
          ? window.matchMedia('(max-width: ' + mobileBreakpoint + 'px)').matches
          : window.innerWidth <= mobileBreakpoint;

        if (isMobileViewport) {
          this.objectFit = mobileObjectFit;
          if (mobileBaseUrl) {
            this.baseUrl = mobileBaseUrl;
            if (mobileFilePrefix) this.filePrefix = mobileFilePrefix;
            if (Number.isFinite(mobileStartFrame)) this.startFrame = mobileStartFrame;
            if (Number.isFinite(mobileEndFrame)) this.endFrame = mobileEndFrame;
          }
        }

        if (!Number.isFinite(this.gridRevealStart)) {
          this.gridRevealStart = 82;
        }

        if (!Number.isFinite(this.startFrame)) this.startFrame = 0;
        if (!Number.isFinite(this.endFrame) || this.endFrame < this.startFrame) this.endFrame = this.startFrame;

        this.frameCount = this.endFrame - this.startFrame + 1;
        this.preloadRadius = Math.min(10, this.frameCount - 1);
        this.maxConcurrentLoads = 4;
        this.frameCache = new Map();
        this.pendingFrames = new Map();
        this.queuedFrames = new Set();
        this.preloadQueue = [];
        this.activeLoads = 0;
        this.currentFrameNumber = this.startFrame;
        this.lastDrawnFrameNumber = null;
        this.scrollStart = 0;
        this.totalScrollDistance = 1;
        this.animationScrollDistance = 1;
        this.viewportWidth = 0;
        this.viewportHeight = 0;
        this.devicePixelRatio = 1;
        this.isVisible = false;
        this.needsMeasure = true;
        this.rafId = 0;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.handleResizeObserved = this.handleResizeObserved.bind(this);

        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
            rootMargin: '250px 0px'
          });
          this.intersectionObserver.observe(this);
        } else {
          this.isVisible = true;
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResizeObserved);
          this.resizeObserver.observe(this.stage);
        }

        this.enqueueFrame(this.startFrame, true);
        this.drainQueue();
        this.requestRender();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        if (this.rafId) {
          window.cancelAnimationFrame(this.rafId);
          this.rafId = 0;
        }

        this.style.removeProperty('--sequence-end-grid-progress');
      }

      handleIntersection(entries) {
        entries.forEach((entry) => {
          if (entry.target !== this) return;

          this.isVisible = entry.isIntersecting;

          if (this.isVisible) {
            this.preloadAround(this.currentFrameNumber);
            this.requestRender();
          }
        });
      }

      handleResize() {
        this.needsMeasure = true;
        this.requestRender();
      }

      handleResizeObserved() {
        this.needsMeasure = true;
        this.requestRender();
      }

      handleScroll() {
        if (!this.isVisible && this.frameCache.size === 0) return;
        this.requestRender();
      }

      requestRender() {
        if (this.rafId) return;

        this.rafId = window.requestAnimationFrame(() => {
          this.rafId = 0;
          this.update();
        });
      }

      update() {
        if (this.needsMeasure) {
          this.measure();
        }

        if (!this.viewportWidth || !this.viewportHeight) return;

        const progress = this.getScrollProgress();
        const nextFrame = this.startFrame + Math.round(progress * (this.frameCount - 1));

        this.currentFrameNumber = nextFrame;
        this.preloadAround(nextFrame);
        this.renderFrame(nextFrame);
        this.updateEndGrid(progress);
      }

      measure() {
        this.needsMeasure = false;

        const stageWidth = this.stage.clientWidth;
        const stageHeight = this.stage.clientHeight;

        if (!stageWidth || !stageHeight) return;

        const nextDevicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const resized =
          stageWidth !== this.viewportWidth ||
          stageHeight !== this.viewportHeight ||
          nextDevicePixelRatio !== this.devicePixelRatio;

        this.viewportWidth = stageWidth;
        this.viewportHeight = stageHeight;
        this.devicePixelRatio = nextDevicePixelRatio;

        if (resized) {
          this.canvas.width = Math.round(stageWidth * nextDevicePixelRatio);
          this.canvas.height = Math.round(stageHeight * nextDevicePixelRatio);
          this.context.setTransform(nextDevicePixelRatio, 0, 0, nextDevicePixelRatio, 0, 0);
          this.lastDrawnFrameNumber = null;
        }

        const scrollRect = this.scrollArea.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const stickyHeight = this.sticky.offsetHeight || stageHeight;
        const holdLengthValue = Number.parseFloat(window.getComputedStyle(this).getPropertyValue('--sequence-end-hold-length'));
        const holdDistance = Number.isFinite(holdLengthValue)
          ? Math.max((window.innerHeight * holdLengthValue) / 100, 0)
          : 0;

        this.scrollStart = scrollRect.top + scrollTop;
        this.totalScrollDistance = Math.max(this.scrollArea.offsetHeight - stickyHeight, 1);
        this.animationScrollDistance = Math.max(this.totalScrollDistance - Math.min(holdDistance, this.totalScrollDistance - 1), 1);
      }

      getScrollProgress() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const rawProgress = (scrollTop - this.scrollStart) / this.animationScrollDistance;

        if (rawProgress <= 0) return 0;
        if (rawProgress >= 1) return 1;

        return rawProgress;
      }

      buildFrameUrl(frameNumber) {
        const normalizedBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl : this.baseUrl + '/';
        const paddedFrame = String(frameNumber).padStart(5, '0');
        const frameUrl = normalizedBaseUrl + this.filePrefix + paddedFrame + '.' + this.fileExtension;

        if (!this.cacheVersion) {
          return frameUrl;
        }

        const separator = frameUrl.includes('?') ? '&' : '?';
        return `${frameUrl}${separator}v=${encodeURIComponent(this.cacheVersion)}`;
      }

      updateEndGrid(progress) {
        if (!this.endGrid) {
          return;
        }

        const revealStart = this.clamp(this.gridRevealStart / 100, 0, 0.98);
        const revealProgress = progress <= revealStart
          ? 0
          : this.clamp((progress - revealStart) / Math.max(1 - revealStart, 0.01), 0, 1);

        this.style.setProperty('--sequence-end-grid-progress', revealProgress.toFixed(4));
      }

      enqueueFrame(frameNumber, priority) {
        if (frameNumber < this.startFrame || frameNumber > this.endFrame) return;
        if (this.frameCache.has(frameNumber) || this.pendingFrames.has(frameNumber) || this.queuedFrames.has(frameNumber)) return;

        if (priority) {
          this.preloadQueue.unshift(frameNumber);
        } else {
          this.preloadQueue.push(frameNumber);
        }

        this.queuedFrames.add(frameNumber);
      }

      preloadAround(frameNumber) {
        for (let offset = 0; offset <= this.preloadRadius; offset += 1) {
          const forwardFrame = frameNumber + offset;
          const backwardFrame = frameNumber - offset;

          this.enqueueFrame(forwardFrame, offset <= 2);

          if (offset > 0) {
            this.enqueueFrame(backwardFrame, offset <= 2);
          }
        }

        this.drainQueue();
      }

      drainQueue() {
        while (this.activeLoads < this.maxConcurrentLoads && this.preloadQueue.length) {
          const nextFrame = this.preloadQueue.shift();
          this.queuedFrames.delete(nextFrame);
          this.activeLoads += 1;

          this.loadFrame(nextFrame).finally(() => {
            this.activeLoads -= 1;
            this.drainQueue();
          });
        }
      }

      loadFrame(frameNumber) {
        if (this.frameCache.has(frameNumber)) {
          return Promise.resolve(this.frameCache.get(frameNumber));
        }

        if (this.pendingFrames.has(frameNumber)) {
          return this.pendingFrames.get(frameNumber);
        }

        const image = new Image();
        image.decoding = 'async';

        const promise = new Promise((resolve) => {
          image.onload = () => {
            this.pendingFrames.delete(frameNumber);
            this.frameCache.set(frameNumber, image);

            if (frameNumber === this.currentFrameNumber || this.lastDrawnFrameNumber === null) {
              this.requestRender();
            }

            resolve(image);
          };

          image.onerror = () => {
            this.pendingFrames.delete(frameNumber);
            resolve(null);
          };

          image.src = this.buildFrameUrl(frameNumber);
        });

        this.pendingFrames.set(frameNumber, promise);
        return promise;
      }

      renderFrame(frameNumber) {
        const frameToDraw = this.findClosestLoadedFrame(frameNumber);

        if (!frameToDraw) return;
        if (frameToDraw.number === this.lastDrawnFrameNumber) return;

        this.drawImage(frameToDraw.image);
        this.lastDrawnFrameNumber = frameToDraw.number;
        this.classList.add('is-ready');

        if (this.loadingIndicator) {
          this.loadingIndicator.hidden = true;
        }
      }

      findClosestLoadedFrame(frameNumber) {
        if (this.frameCache.has(frameNumber)) {
          return {
            number: frameNumber,
            image: this.frameCache.get(frameNumber)
          };
        }

        for (let distance = 1; distance < this.frameCount; distance += 1) {
          const previousFrame = frameNumber - distance;
          const nextFrame = frameNumber + distance;

          if (previousFrame >= this.startFrame && this.frameCache.has(previousFrame)) {
            return {
              number: previousFrame,
              image: this.frameCache.get(previousFrame)
            };
          }

          if (nextFrame <= this.endFrame && this.frameCache.has(nextFrame)) {
            return {
              number: nextFrame,
              image: this.frameCache.get(nextFrame)
            };
          }
        }

        return null;
      }

      drawImage(image) {
        const context = this.context;
        const stageWidth = this.viewportWidth;
        const stageHeight = this.viewportHeight;
        const imageWidth = image.naturalWidth || stageWidth;
        const imageHeight = image.naturalHeight || stageHeight;

        if (!stageWidth || !stageHeight || !imageWidth || !imageHeight) return;

        const scale = this.objectFit === 'cover'
          ? Math.max(stageWidth / imageWidth, stageHeight / imageHeight)
          : Math.min(stageWidth / imageWidth, stageHeight / imageHeight);

        const drawWidth = imageWidth * scale;
        const drawHeight = imageHeight * scale;
        const offsetX = (stageWidth - drawWidth) / 2;
        const offsetY = (stageHeight - drawHeight) / 2;

        context.clearRect(0, 0, stageWidth, stageHeight);
        context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}